import { useEffect, useState } from "react"
import { MessageSquare } from "lucide-react"
import { toast } from "sonner"
import { CommentComposer } from "@/modules/recruitment/applications/ui/components/comments/CommentComposer"
import { CommentItem } from "@/modules/recruitment/applications/ui/components/comments/CommentItem"
import {
  useApplicationComments,
  useApplicationCommentsLiveSync,
} from "@/modules/recruitment/applications/hooks/useApplicationComments"
import { useApplicationHub } from "@/modules/recruitment/applications/hooks/useApplicationHub"

const PAGE_SIZE = 5

interface CommentThreadProps {
  organizationId: string
  applicationId: string
  highlightCommentId?: string
  readOnly?: boolean
}

export function CommentThread({ organizationId, applicationId, highlightCommentId, readOnly = false }: CommentThreadProps) {
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)
  const { data: comments, isLoading, isError } = useApplicationComments(organizationId, applicationId)

  useApplicationCommentsLiveSync(organizationId, applicationId)

  const { onMentioned } = useApplicationHub()
  useEffect(() => {
    return onMentioned((mention) => {
      if (mention.applicationId !== applicationId) return
      toast(`${mention.mentionedByName} mentioned you in this note thread`)
    })
  }, [onMentioned, applicationId])

  useEffect(() => {
    if (!highlightCommentId || !comments) return
    const index = comments.findIndex(
      (c) => c.id === highlightCommentId || c.replies.some((r) => r.id === highlightCommentId),
    )
    if (index >= 0 && index >= visibleCount) {
      setVisibleCount(index + 1)
    }
  }, [highlightCommentId, comments, visibleCount])

  if (isLoading) {
    return (
      <div className="space-y-4 py-4">
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex gap-3 animate-pulse">
            <div className="size-8.5 rounded-full bg-muted flex-none" />
            <div className="flex-1 space-y-2">
              <div className="h-3 w-32 bg-muted rounded" />
              <div className="h-3 w-full bg-muted rounded" />
            </div>
          </div>
        ))}
      </div>
    )
  }

  if (isError) {
    return (
      <div className="py-12 text-center text-muted-foreground">
        <p className="text-sm">Couldn&apos;t load comments. Try refreshing the page.</p>
      </div>
    )
  }

  const allComments = comments ?? []
  const visibleComments = allComments.slice(0, visibleCount)
  const remainingCount = allComments.length - visibleComments.length

  return (
    <div className="space-y-6">
      <CommentComposer organizationId={organizationId} applicationId={applicationId} readOnly={readOnly} />

      {allComments.length === 0 ? (
        <div className="py-12 text-center text-muted-foreground">
          <div className="bg-muted size-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <MessageSquare className="size-8 text-muted-foreground" />
          </div>
          <p className="font-medium text-foreground">No comments yet</p>
          <p className="text-sm mt-1">Be the first to leave a note on this candidate.</p>
        </div>
      ) : (
        <>
          <div className="space-y-5">
            {visibleComments.map((comment) => (
              <CommentItem
                key={comment.id}
                comment={comment}
                organizationId={organizationId}
                applicationId={applicationId}
                highlightCommentId={highlightCommentId}
                readOnly={readOnly}
              />
            ))}
          </div>

          <div className="flex gap-2">
            {remainingCount > 0 && (
              <button
                type="button"
                onClick={() => setVisibleCount((v) => v + PAGE_SIZE)}
                className="text-xs font-medium border border-border text-primary px-4 py-2 rounded-md"
              >
                Show more ({remainingCount})
              </button>
            )}
            {visibleCount > PAGE_SIZE && (
              <button
                type="button"
                onClick={() => setVisibleCount(PAGE_SIZE)}
                className="text-xs font-medium border border-border text-muted-foreground px-4 py-2 rounded-md"
              >
                Show less
              </button>
            )}
          </div>
        </>
      )}
    </div>
  )
}
